
import React, { useState, useEffect } from "react";
import { Box, Typography, makeStyles, Chip, CircularProgress } from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import AppButton from "components/CustomButtons/AppButton";
import AppDialog from "components/Dialog/AppDialog";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import PreviewDialog from "components/Dialog/PreviewDialog";
import AppColor from "utilities/AppColor";
import IncidentActionForm from "./form/IncidentActionForm";
import {
  fetchIncidentDetail,
  updateIncidentStatus,
  fetchIncidentEvidence,
  uploadIncidentEvidence,
  deleteIncidentEvidence,
  getIncidentDetail,
} from "store/slices/incident";
import {
  fetchPageAction,
  createAction,
  updateActionStatus,
  getActionList,
} from "store/slices/action";
import { fetchCompanyUserListPaging, getCompanyUserListPaging } from "store/slices/companyUser";
import { fetchWorkerListPaging, getWorkerList } from "store/slices/worker";
import { fetchClientDetail, getClientDetail } from "store/slices/client";
import { getAuthInfo } from "store/slices/auth";
import { INCIDENT_STATUS, INCIDENT_STATUS_ORDER, INCIDENT_SEVERITY, SYSTEM_ROLE, ACTION_SOURCE, ACTION_OWNER, ACTION_STATUS } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  container: {
    backgroundColor: '#F5F5F5',
    minHeight: '100vh',
  },
  navBreadcrumb: {
    padding: "16px 24px",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff",
  },
  breadcrumbLink: {
    fontSize: "13px",
    color: "#1D9E75",
    textDecoration: "none",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
  },
  breadcrumbDivider: {
    color: "#6B7280",
    fontSize: "13px",
  },
  breadcrumbText: {
    fontSize: "13px",
    color: "#6B7280",
  },
  notifiableBanner: {
    background: "#FEE2E2",
    borderBottom: "1px solid #F09595",
    padding: "12px 24px",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  pageHeader: {
    padding: "24px 24px 0",
  },
  pageSubtitle: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#6B7280",
    letterSpacing: "0.04em",
    marginBottom: "4px",
  },
  pageTitle: {
    fontSize: "20px",
    fontWeight: 500,
    marginBottom: "10px",
    color: "#111827",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  metaItem: {
    fontSize: "13px",
    color: "#6B7280",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pageBody: {
    padding: "20px 24px",
  },
  card: {
    background: "#fff",
    borderRadius: "12px",
    border: "0.5px solid #E5E5EB",
    padding: "20px",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "16px",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 24px",
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
  metaLabel: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#6B7280",
    letterSpacing: "0.04em",
    marginBottom: "4px",
    textTransform: "uppercase",
  },
  metaValue: {
    fontSize: "13px",
    color: "#111827",
    lineHeight: "1.6",
  },
  workflow: {
    display: "flex",
    alignItems: "flex-start",
    position: "relative",
  },
  stepCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  },
  stepCircle: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 500,
    zIndex: 1,
    position: "relative",
  },
  stepDone: {
    background: "#1D9E75",
    color: "#fff",
  },
  stepPending: {
    background: "#fff",
    border: "2px solid #E5E5EB",
    color: "#6B7280",
  },
  stepClickable: {
    cursor: "pointer",
  },
  stepLine: {
    height: "2px",
    width: "calc(100% - 32px)",
    position: "absolute",
    top: "16px",
    left: "calc(50% + 16px)",
    zIndex: 0,
  },
  stepLabel: {
    fontSize: "11px",
    marginTop: "6px",
    textAlign: "center",
  },
  workflowNote: {
    marginTop: "14px",
    paddingTop: "12px",
    borderTop: "0.5px solid #E5E5EB",
    fontSize: "12px",
    color: "#6B7280",
  },
  uploadZone: {
    border: "1.5px dashed #E5E5EB",
    borderRadius: "8px",
    padding: "32px",
    textAlign: "center",
  },
  uploadIcon: {
    fontSize: "28px",
    color: "#6B7280",
  },
  uploadText: {
    fontSize: "13px",
    color: "#6B7280",
    marginTop: "8px",
  },
  uploadSubtext: {
    fontSize: "11px",
    color: "#6B7280",
    marginTop: "4px",
  },
  footerActions: {
    padding: "0 24px 32px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },
  th: {
    textAlign: "left",
    padding: "8px 0",
    fontSize: "11px",
    fontWeight: 500,
    color: "#6B7280",
    borderBottom: "0.5px solid #E5E5EB",
  },
  td: {
    padding: "10px 0",
    color: "#111827",
    borderBottom: "0.5px solid #F3F4F6",
  },
  emptyTableText: {
    textAlign: "center",
    color: "#6B7280",
    padding: "24px 0",
  },
}));

// Mirrors the severity badge colors used in the incident register (IncidentTable.js)
// so the "Low/Moderate/Serious/Notifiable" chip looks the same everywhere.
const severityColors = {
  [INCIDENT_SEVERITY.LOW]: { background: "#DBEAFE", color: "#1E40AF" },
  [INCIDENT_SEVERITY.MODERATE]: { background: "#FEF3C7", color: "#92400E" },
  [INCIDENT_SEVERITY.SERIOUS]: { background: "#FEF3C7", color: "#92400E" },
  [INCIDENT_SEVERITY.NOTIFIABLE]: { background: "#FEE2E2", color: "#991b1b" },
};

const severityLabels = {
  [INCIDENT_SEVERITY.LOW]: "Low",
  [INCIDENT_SEVERITY.MODERATE]: "Moderate",
  [INCIDENT_SEVERITY.SERIOUS]: "Serious",
  [INCIDENT_SEVERITY.NOTIFIABLE]: "Notifiable",
};

// "Commission reported" is not a real backend status — it's a manual, admin-only
// flag (incident.commissionReported) shown as a step between Under review and Resolved.
const WORKFLOW_STEPS = [
  { key: INCIDENT_STATUS.OPEN, label: "Open", kind: "status" },
  { key: INCIDENT_STATUS.UNDER_REVIEW, label: "Under review", kind: "status" },
  { key: "COMMISSION_REPORTED", label: "Commission reported", kind: "commission" },
  { key: INCIDENT_STATUS.RESOLVED, label: "Resolved", kind: "status" },
  { key: INCIDENT_STATUS.CLOSED, label: "Closed", kind: "status" },
];

const formatDateTime = (dateValue) => {
  if (!dateValue) return "-";
  const d = moment(dateValue);
  if (!d.isValid()) return dateValue;
  return d.format("DD MMMM YYYY, h:mm a");
};

const formatType = (incident) => {
  if (!incident) return "-";
  if (incident.incidentTypes && incident.incidentTypes.length) {
    return incident.incidentTypes
      .map((t) => (t || "").toString().replace(/_/g, " ").toLowerCase())
      .join(", ");
  }
  return incident.incidentTypeOther || "-";
};

const formatActionDueDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const getActionStatusColor = (status) => {
  switch (status) {
    case ACTION_STATUS.COMPLETED:
      return { background: "#ECFDF5", color: "#047857" };
    case ACTION_STATUS.IN_PROGRESS:
      return { background: "#FEF3C7", color: "#D97706" };
    case ACTION_STATUS.OVERDUE:
      return { background: "#FEE2E2", color: "#B91C1C" };
    case ACTION_STATUS.OPEN:
    default:
      return { background: "#EFF6FF", color: "#1D4ED8" };
  }
};

const getActionStatusLabel = (status) => {
  switch (status) {
    case ACTION_STATUS.COMPLETED:
      return "Completed";
    case ACTION_STATUS.IN_PROGRESS:
      return "In Progress";
    case ACTION_STATUS.OVERDUE:
      return "Overdue";
    case ACTION_STATUS.OPEN:
      return "Open";
    default:
      return status || "";
  }
};

const IncidentDetail = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showNotifiableBanner, setShowNotifiableBanner] = useState(true);
  const [uploadedEvidences, setUploadedEvidences] = useState([]);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);

  const [openEditActionDialog, setOpenEditActionDialog] = useState(false);
  const [editingAction, setEditingAction] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [deleteEvidenceTarget, setDeleteEvidenceTarget] = useState(null);

  const [openCommissionDialog, setOpenCommissionDialog] = useState(false);

  const incident = useSelector(getIncidentDetail);
  const actions = useSelector(getActionList);
  const userInfo = useSelector(getAuthInfo);
  const companyUsers = useSelector(getCompanyUserListPaging);
  const workers = useSelector(getWorkerList);
  const clientDetail = useSelector(getClientDetail);
  const isAdmin = !!userInfo && userInfo.role === SYSTEM_ROLE.COMPANY;

  useEffect(() => {
    if (id) {
      dispatch(fetchIncidentDetail({ id }));
      dispatch(fetchPageAction({ source: ACTION_SOURCE.INCIDENT, incidentId: id, page: 1, size: 50 }));
      dispatch(fetchIncidentEvidence({ id })).then((res) => {
        if (res.payload) {
          setUploadedEvidences(res.payload);
        }
      });
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (userInfo && userInfo.companyId) {
      dispatch(fetchCompanyUserListPaging({ company_id: userInfo.companyId, page_number: 1, page_size: 1000 }));
      dispatch(fetchWorkerListPaging({ companyId: userInfo.companyId, page: 1, size: 1000 }));
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    if (incident && incident.participantId && incident.companyId) {
      dispatch(fetchClientDetail({ clientId: incident.participantId, companyId: incident.companyId }));
    }
  }, [dispatch, incident.participantId, incident.companyId]);

  const companyUserOptions = (companyUsers || []).map((u) => ({
    value: u.id,
    label: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
  }));

  const workerOptions = (workers || []).map((w) => ({
    value: w.id,
    label: w.name || `${w.firstName || ""} ${w.lastName || ""}`.trim(),
  }));

  const worker = incident.workerId ? (workers || []).find((w) => w.id === incident.workerId) : null;
  const workerName = worker ? (worker.name || `${worker.firstName || ""} ${worker.lastName || ""}`.trim()) : null;

  const participantClient = clientDetail && clientDetail.client;
  const participantName =
    participantClient && participantClient.id === incident.participantId
      ? `${participantClient.firstName || ""} ${participantClient.lastName || ""}`.trim()
      : null;

  useEffect(() => {
    setShowNotifiableBanner(
      incident && incident.severity === INCIDENT_SEVERITY.NOTIFIABLE && !incident.commissionReported
    );
  }, [incident]);

  const currentStatusIndex = INCIDENT_STATUS_ORDER.indexOf(incident.status);

  const workflowStepStates = WORKFLOW_STEPS.map((step) => {
    if (step.kind === "commission") {
      const resolvedIndex = INCIDENT_STATUS_ORDER.indexOf(INCIDENT_STATUS.RESOLVED);
      const isLineGreen = currentStatusIndex >= resolvedIndex;
      const isDone = isLineGreen || !!incident.commissionReported;
      const isNext = incident.status === INCIDENT_STATUS.UNDER_REVIEW && !incident.commissionReported;
      return { step, isDone, isLineGreen, isNext };
    }
    const statusIndex = INCIDENT_STATUS_ORDER.indexOf(step.key);
    const isDone = statusIndex <= currentStatusIndex;
    const isLineGreen = statusIndex < currentStatusIndex;
    const isNext = statusIndex === currentStatusIndex + 1;
    return { step, isDone, isLineGreen, isNext };
  });

  const lastDoneWorkflowStep = [...workflowStepStates].reverse().find((s) => s.isDone);
  const currentWorkflowLabel = lastDoneWorkflowStep ? lastDoneWorkflowStep.step.label : "-";

  const handleAdvanceStatus = (nextStatus) => {
    const nextIndex = INCIDENT_STATUS_ORDER.indexOf(nextStatus);
    if (nextIndex !== currentStatusIndex + 1) return;

    const requiresCommissionConfirm =
      nextStatus === INCIDENT_STATUS.RESOLVED &&
      incident.severity === INCIDENT_SEVERITY.NOTIFIABLE &&
      !incident.commissionReported;

    if (requiresCommissionConfirm) {
      const confirmed = window.confirm(
        "This is a Notifiable incident. Advancing to Resolved will also mark it as reported to the NDIS Commission. Continue?"
      );
      if (!confirmed) return;
      dispatch(updateIncidentStatus({ id, status: nextStatus, commissionReported: true }));
    } else {
      dispatch(updateIncidentStatus({ id, status: nextStatus }));
    }
  };

  const handleMarkCommissionReported = () => {
    setOpenCommissionDialog(true);
  };

  const confirmMarkCommissionReported = () => {
    dispatch(updateIncidentStatus({ id, status: incident.status, commissionReported: true }));
    setOpenCommissionDialog(false);
  };

  const handleAddAction = (actionName, dueDate, owner, assignedToUserId, file) => {
    return dispatch(createAction({
      source: ACTION_SOURCE.INCIDENT,
      linkedRecordId: id,
      actionDescription: actionName,
      owner,
      assignedToUserId: (owner === ACTION_OWNER.WORKER || owner === ACTION_OWNER.COMPANY) ? assignedToUserId : null,
      dueDate: dueDate ? `${dueDate} 23:59` : null,
      file,
    })).then(() => {
      dispatch(fetchPageAction({ source: ACTION_SOURCE.INCIDENT, incidentId: id, page: 1, size: 50 }));
      setIsModalOpen(false);
    });
  };

  const handleOpenEditAction = (action) => {
    setEditingAction(action);
    setOpenEditActionDialog(true);
  };

  const handleUpdateAction = (actionName, dueDate, owner, assignedToUserId, file, status) => {
    return dispatch(updateActionStatus({
      id: editingAction.id,
      status,
      actionDescription: actionName,
      owner,
      assignedToUserId: (owner === ACTION_OWNER.WORKER || owner === ACTION_OWNER.COMPANY) ? assignedToUserId : null,
      dueDate: dueDate ? `${dueDate} 23:59` : null,
      file,
    })).then(() => {
      dispatch(fetchPageAction({ source: ACTION_SOURCE.INCIDENT, incidentId: id, page: 1, size: 50 }));
      setOpenEditActionDialog(false);
    });
  };

  const handleEvidenceUpload = (files) => {
    if (!files || !files.length) return;
    setIsUploadingEvidence(true);
    dispatch(uploadIncidentEvidence({ id, files })).then((res) => {
      if (res.payload && res.payload.length) {
        setUploadedEvidences((prev) => [...prev, ...res.payload]);
      }
    }).finally(() => {
      setIsUploadingEvidence(false);
    });
  };

  const handleDeleteEvidence = (evidence) => {
    setDeleteEvidenceTarget(evidence);
  };

  const confirmDeleteEvidence = () => {
    const evidence = deleteEvidenceTarget;
    if (!evidence) return;
    dispatch(deleteIncidentEvidence({ id, evidenceId: evidence.id })).then((res) => {
      if (res.payload) {
        setUploadedEvidences((prev) => prev.filter((item) => item.id !== evidence.id));
      }
    }).finally(() => {
      setDeleteEvidenceTarget(null);
    });
  };

  const handleDownloadEvidence = async (evidence) => {
    try {
      const response = await fetch(evidence.fileUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = evidence.fileName || `Evidence-${evidence.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      window.open(evidence.fileUrl, "_blank");
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.navBreadcrumb}>
        <Box
          className={classes.breadcrumbLink}
          onClick={() => history.push("/admin/incidents")}
        >
          <i className="fas fa-arrow-left" style={{ fontSize: "13px" }}></i>
          Incident register
        </Box>
        <span className={classes.breadcrumbDivider}>/</span>
        <span className={classes.breadcrumbText}>{incident.incidentNumber || `INC-${id}`}</span>
      </Box>

      {showNotifiableBanner && (
        <Box className={classes.notifiableBanner}>
          <i
            className="fas fa-exclamation-triangle"
            style={{ fontSize: "16px", color: "#DC2626", marginTop: "1px", flexShrink: 0 }}
          ></i>
          <Box>
            <Box style={{ fontSize: "13px", fontWeight: 500, color: "#991B1B" }}>
              Notifiable incident — 24-hour reporting required
            </Box>
            <Box style={{ fontSize: "12px", color: "#DC2626", marginTop: "2px" }}>
              Commission report due within 24 hours. Admin has been notified by email.
            </Box>
          </Box>
          <i
            className="fas fa-times"
            style={{ fontSize: "14px", color: "#DC2626", marginLeft: "auto", cursor: "pointer", flexShrink: 0 }}
            onClick={() => setShowNotifiableBanner(false)}
          ></i>
        </Box>
      )}

      <Box className={classes.pageHeader}>
        <Typography className={classes.pageSubtitle}>{incident.incidentNumber || `INC-${id}`}</Typography>
        <Typography className={classes.pageTitle} style={{ textTransform: "capitalize" }}>
          {formatType(incident)}
        </Typography>
        <Box className={classes.metaRow}>
          <span className={classes.metaItem}>
            <i className="fas fa-clock" style={{ fontSize: "13px" }}></i> {formatDateTime(incident.incidentDate)}
          </span>
          <span className={classes.metaItem}>
            <i className="fas fa-home" style={{ fontSize: "13px" }}></i> {incident.location || "-"}
          </span>
          <span className={classes.metaItem}>
            <i className="fas fa-wheelchair" style={{ fontSize: "13px" }}></i> {incident.participantId ? (participantName || `Participant #${incident.participantId}`) : "-"}
          </span>
          {incident.workerId && (
            <span className={classes.metaItem}>
              <i className="fas fa-user" style={{ fontSize: "13px" }}></i> {workerName || `Worker #${incident.workerId}`}
            </span>
          )}
        </Box>
      </Box>

      <Box className={classes.pageBody}>
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle} style={{ color: "#6B7280" }}>
            Status workflow
          </Typography>
          <Box className={classes.workflow}>
            {workflowStepStates.map(({ step, isDone, isLineGreen, isNext }, index) => {
              const onClick =
                step.kind === "commission"
                  ? (isNext && isAdmin ? handleMarkCommissionReported : undefined)
                  : (isNext ? () => handleAdvanceStatus(step.key) : undefined);

              return (
                <Box className={classes.stepCol} key={step.key}>
                  <Box
                    className={`${classes.stepCircle} ${isDone ? classes.stepDone : classes.stepPending} ${onClick ? classes.stepClickable : ""}`}
                    onClick={onClick}
                  >
                    {isDone ? <i className="fas fa-check" style={{ fontSize: "14px" }}></i> : index + 1}
                  </Box>
                  {index < WORKFLOW_STEPS.length - 1 && (
                    <Box
                      className={classes.stepLine}
                      style={{ background: isLineGreen ? "#1D9E75" : "#E5E5EB" }}
                    ></Box>
                  )}
                  <Typography
                    className={classes.stepLabel}
                    style={{ color: isDone ? "#1D9E75" : "#6B7280", fontWeight: isDone ? 500 : 400 }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Typography className={classes.workflowNote}>
            Click the next step to advance the status. Current: <span style={{ color: "#1D9E75", fontWeight: 500 }}>{currentWorkflowLabel}</span>
            {incident.status === INCIDENT_STATUS.UNDER_REVIEW && !incident.commissionReported && (
              <span style={{ marginLeft: "10px", color: "#DC2626" }}>⚠ Step 3 (Commission reported) must be advanced manually by admin only.</span>
            )}
          </Typography>
        </Box>

        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>
            <i className="fas fa-info-circle" style={{ color: "#1D9E75" }}></i> Incident information
          </Typography>
          <Box className={classes.grid2}>
            <Box>
              <Typography className={classes.metaLabel}>Incident type</Typography>
              <Typography className={classes.metaValue} style={{ textTransform: "capitalize" }}>{formatType(incident)}</Typography>
            </Box>
            <Box>
              <Typography className={classes.metaLabel}>Severity</Typography>
              <Box>
                <Chip
                  size="small"
                  label={severityLabels[incident.severity] || incident.severity || "-"}
                  style={{
                    background: (severityColors[incident.severity] || {}).background || "#F3F4F6",
                    color: (severityColors[incident.severity] || {}).color || "#6B7280",
                    fontSize: "12px",
                  }}
                />
              </Box>
            </Box>
            <Box>
              <Typography className={classes.metaLabel}>Injury / harm</Typography>
              <Typography className={classes.metaValue}>{incident.injuryOccurred ? "Yes" : "No"}</Typography>
            </Box>
            <Box>
              <Typography className={classes.metaLabel}>Witnesses</Typography>
              <Typography className={classes.metaValue}>{incident.witnesses || "-"}</Typography>
            </Box>
            <Box className={classes.fullWidth}>
              <Typography className={classes.metaLabel}>Description</Typography>
              <Typography className={classes.metaValue}>
                {incident.antecedent || "-"}{incident.behaviour ? ` ${incident.behaviour}` : ""}
              </Typography>
            </Box>
            <Box className={classes.fullWidth}>
              <Typography className={classes.metaLabel}>Immediate action taken</Typography>
              <Typography className={classes.metaValue}>
                {incident.consequence || "-"}
              </Typography>
            </Box>
            {incident.injuryOccurred && (
              <Box className={classes.fullWidth}>
                <Typography className={classes.metaLabel}>Injury details</Typography>
                <Typography className={classes.metaValue}>
                  {incident.injuryDetails || "-"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box className={classes.card}>
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              <i className="fas fa-tasks" style={{ color: "#1D9E75" }}></i> Corrective action log
            </Typography>
            <AppButton
              label="Add action"
              buttonColor={AppColor.mainColor}
              style={{ fontSize: "13px", padding: "8px 16px", borderRadius: "8px" }}
              onClick={() => setIsModalOpen(true)}
            />
          </Box>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Action</th>
                <th className={classes.th}>Assigned to</th>
                <th className={classes.th}>Due date</th>
                <th className={classes.th}>Status</th>
                <th className={classes.th} style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {actions.length === 0 && (
                <tr>
                  <td colSpan="5" className={classes.emptyTableText}>
                    No corrective actions logged yet.
                  </td>
                </tr>
              )}
              {actions.map((action) => (
                <tr key={action.id}>
                  <td className={classes.td}>{action.actionDescription}</td>
                  <td className={classes.td}>{action.assignedToName || (action.assignedToUserId ? `User #${action.assignedToUserId}` : "-")}</td>
                  <td className={classes.td}>{action.dueDate ? moment(action.dueDate).format("DD/MM/YYYY") : "-"}</td>
                  <td className={classes.td}>
                    <Chip
                      size="small"
                      label={getActionStatusLabel(action.status)}
                      style={{ ...getActionStatusColor(action.status), fontWeight: 500 }}
                    />
                  </td>
                  <td className={classes.td}>
                    <Box style={{ display: "flex", alignItems: "center", gap: "20px", justifyContent: "flex-end" }}>
                      {action.fileUrl && (
                        <>
                          <i
                            className="fas fa-eye"
                            title="View"
                            style={{ fontSize: "13px", color: "#1D9E75", cursor: "pointer" }}
                            onClick={() => {
                              setPreviewFileUrl(action.fileUrl);
                              setPreviewOpen(true);
                            }}
                          />
                          <a
                            href={action.fileUrl}
                            download={action.fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "none", color: "inherit", display: "inline-flex" }}
                          >
                            <i
                              className="fas fa-download"
                              title="Download"
                              style={{ fontSize: "13px", color: "#1D9E75", cursor: "pointer" }}
                            />
                          </a>
                        </>
                      )}
                      <i
                        className="fas fa-pen"
                        title="Edit"
                        style={{ fontSize: "13px", color: "#1D9E75", cursor: "pointer" }}
                        onClick={() => handleOpenEditAction(action)}
                      />
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>
            <i className="fas fa-paperclip" style={{ color: "#1D9E75" }}></i> Evidence and attachments
          </Typography>
          <Box
            className={classes.uploadZone}
            style={{ cursor: isUploadingEvidence ? "default" : "pointer" }}
            onClick={() => !isUploadingEvidence && document.getElementById("incident-evidence-input").click()}
          >
            {isUploadingEvidence ? (
              <>
                <CircularProgress size={24} style={{ color: "#1D9E75" }} />
                <Typography className={classes.uploadText} style={{ marginTop: 8 }}>
                  Uploading...
                </Typography>
              </>
            ) : (
              <>
                <i className={`fas fa-upload ${classes.uploadIcon}`}></i>
                <Typography className={classes.uploadText}>
                  Drag and drop files here or <span style={{ color: "#1D9E75", cursor: "pointer" }}>browse</span>
                </Typography>
                <Typography className={classes.uploadSubtext}>
                  Supports JPG, PNG, PDF, DOCX — max 20MB each
                </Typography>
              </>
            )}
            <input
              id="incident-evidence-input"
              type="file"
              multiple
              style={{ display: "none" }}
              accept=".jpg,.jpeg,.png,.pdf,.docx"
              onChange={(e) => handleEvidenceUpload(e.target.files)}
              disabled={isUploadingEvidence}
            />
          </Box>
          {uploadedEvidences.length > 0 && (
            <Box style={{ marginTop: 12 }}>
              {uploadedEvidences.map((evidence) => (
                <Box
                  key={evidence.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 13,
                    padding: "6px 0",
                    borderBottom: "0.5px solid #F3F4F6",
                  }}
                >
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {evidence.fileName || evidence.description || `Evidence #${evidence.id}`}
                  </span>
                  <Box style={{ display: "flex", alignItems: "center", gap: "20px", flexShrink: 0, marginLeft: 12 }}>
                    <i
                      className="fas fa-eye"
                      title="View"
                      style={{ fontSize: "13px", color: "#1D9E75", cursor: "pointer" }}
                      onClick={() => {
                        setPreviewFileUrl(evidence.fileUrl);
                        setPreviewOpen(true);
                      }}
                    />
                    <i
                      className="fas fa-download"
                      title="Download"
                      style={{ fontSize: "13px", color: "#1D9E75", cursor: "pointer" }}
                      onClick={() => handleDownloadEvidence(evidence)}
                    />
                    <i
                      className="fas fa-trash"
                      title="Delete"
                      style={{ fontSize: "13px", color: "#A32D2D", cursor: "pointer" }}
                      onClick={() => handleDeleteEvidence(evidence)}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box className={classes.footerActions}>
        <AppButton
          label="Export to PDF"
          buttonColor="transparent"
          style={{ color: "#F59E0B", border: "1px solid #F59E0B", background: "white", fontSize: 11, padding: "4px 10px" }}
          startIcon={<i className="fas fa-file-export" />}
          rounder
        />
        <AppButton
          label="Back to register"
          buttonColor={AppColor.white}
          style={{
            fontSize: "13px",
            padding: "8px 16px",
            borderRadius: "8px",
            border: "0.5px solid #E5E5EB",
            color: "#6B7280",
          }}
          onClick={() => history.push("/admin/incidents")}
        />

        <IncidentActionForm
          open={isModalOpen}
          companyUserOptions={companyUserOptions}
          workerOptions={workerOptions}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleAddAction}
        />
      </Box>

      <IncidentActionForm
        isEdit
        open={openEditActionDialog}
        initialAction={editingAction ? editingAction.actionDescription : ''}
        initialDueDate={editingAction ? formatActionDueDate(editingAction.dueDate) : ''}
        initialOwner={editingAction ? editingAction.owner : ''}
        initialAssignedToUserId={editingAction ? editingAction.assignedToUserId : ''}
        initialFileName={editingAction ? editingAction.fileName : ''}
        initialStatus={editingAction ? editingAction.status : ''}
        companyUserOptions={companyUserOptions}
        workerOptions={workerOptions}
        onClose={() => setOpenEditActionDialog(false)}
        onConfirm={handleUpdateAction}
      />

      <PreviewDialog
        open={previewOpen}
        setOpen={setPreviewOpen}
        fileUrl={previewFileUrl}
      />

      <AppConfirmDialog
        open={!!deleteEvidenceTarget}
        onConfirm={confirmDeleteEvidence}
        onClose={() => setDeleteEvidenceTarget(null)}
        title="Delete Evidence"
        content={`Delete "${deleteEvidenceTarget ? (deleteEvidenceTarget.fileName || `Evidence #${deleteEvidenceTarget.id}`) : ""}"? This cannot be undone.`}
        type="danger"
      />

      <AppDialog
        open={openCommissionDialog}
        onClose={() => setOpenCommissionDialog(false)}
        title="Mark Commission Reported"
        maxWidth="sm"
        actions={[
          {
            label: "Cancel",
            onPress: () => setOpenCommissionDialog(false),
            buttonColor: "#f0143c",
          },
          {
            label: "Confirm",
            onPress: confirmMarkCommissionReported,
            buttonColor: AppColor.mainColor,
          },
        ]}
      >
        <Box>
          <Typography style={{ fontSize: 14, color: "#374151" }}>
            Mark this incident as reported to the NDIS Commission? This step can only be advanced by an admin and cannot be undone.
          </Typography>
        </Box>
      </AppDialog>
    </Box>
  );
};

export default IncidentDetail;
